import {Button, Dialog, DialogContent, MenuItem, Typography} from "@mui/material";
import React, {useState} from "react";
import CCInputLabel from "../Common/CCInputLabel";
import CCSelect from "../Common/CCSelect";
import PersonaSelect from "./PersonaSelect";
import css_shared from "./css/LoginCommon.module.scss";
import css_nav from "../Navigation/css/NavBar.module.scss";
import css_buttons from "../Common/css/CCButton.module.scss";
import parkourImage from "../../assets/ParkourLogo.svg";
import {Blue3} from "../../theme/styles";

/**
 * Shown when a hand-off from another app resolves to an email that belongs to MORE THAN ONE
 * ACCOUNT, and there is no way to know which one the user meant.
 *
 * WHY THIS HAD TO EXIST. The exchange has always answered this case correctly -- 200 with
 * `{Code: "CC-ADC7-49A2", Accounts: {...}}`, the same contract `authenticate` uses, expecting the
 * frontend to show a picker and re-submit with an explicit `Visibility`. Login2 has rendered that
 * picker for ordinary logins since forever. The SSO path never did: ssoAuthenticate understood
 * only the single-`Account` shape and treated everything else as a failure, so a person in two
 * tenants was told their sign-in link had expired -- on a 200, every time, with nothing wrong.
 *
 * A PERSONA IS CHOSEN HERE TOO, because the response carries each account's personas and the
 * Visibility the backend wants names all three parts: `<accountUSID>:<Mode>:<USID>`. PersonaSelect
 * already builds exactly that string from a `valuePrefix`, which is why this is mostly wiring.
 *
 * ONE ACCOUNT WITH SEVERAL PERSONAS (CC-E5EB-43D5) comes through the same door and drops the
 * account dropdown, matching what Login2 shows for the same code.
 */
export type SsoAccounts = Record<string, any>;

export function defaultVisibility(accounts: SsoAccounts, accountName: string): string {
    const account = accounts?.[accountName];
    if (!account) return "";
    const usid = account["AccountUSID"];
    for (const [mode, key] of [["Position", "Positions"], ["UserGroup", "UserGroups"], ["WorkGroup", "WorkGroups"]]) {
        const entries = account[key] ?? [];
        if (entries.length > 0) return `${usid}:${mode}:${entries[0]["USID"]}`;
    }
    return "";
}

/** The account the source app proposed, when it is one of the accounts on offer here. */
export function preferredName(accounts: SsoAccounts, preferredAccountUsid?: string | null): string {
    const names = Object.keys(accounts ?? {});
    if (preferredAccountUsid) {
        const match = names.find(name => accounts[name]?.["AccountUSID"] === preferredAccountUsid);
        if (match) return match;
    }
    return names[0] ?? "";
}

export default function SelectSsoAccountDialog(
    {accounts, submitting, preferredAccountUsid, onChoose}: {
        accounts: SsoAccounts;
        submitting: boolean;
        preferredAccountUsid?: string | null;
        onChoose: (visibility: string) => void;
    },
) {
    const names = Object.keys(accounts ?? {});
    // DEFAULTS TO THE ONE THEY CAME FROM. Reaching the chooser at all means the hint was not
    // enough -- usually a persona that does not exist here -- but the ACCOUNT half of it is still
    // good, and starting on a different tenant than the one they were just working in is a worse
    // guess than the first key in a map.
    const initial = preferredName(accounts, preferredAccountUsid);
    const [accountName, setAccountName] = useState<string>(initial);
    const [visibility, setVisibility] = useState<string>(() => defaultVisibility(accounts, initial));

    const account = accounts?.[accountName];
    const several = names.length > 1;

    return (
        <Dialog maxWidth="md" open={true} scroll="body">
            <DialogContent style={{padding: "0 16px", minWidth: "360px"}}>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
                    <div key={1} style={{paddingTop: "0px", paddingRight: "2px", marginLeft: "-5px"}}><img className={css_nav.ParkourLogo} src={parkourImage} alt="Home"/></div>
                    <div key={2} style={{paddingTop: "5px", marginLeft: "57px", marginRight: "4px"}}><Typography style={{fontSize: 24, fontWeight: 400, color: Blue3}}>|</Typography></div>
                    <div key={3} style={{paddingTop: "7px"}}><Typography style={{fontSize: 24, fontWeight: 400, color: Blue3}}>&nbsp;&nbsp;ParkourAI</Typography></div>
                </div>
                <Typography className={css_shared.Title}>
                    {several ? "Select your account and persona" : "Select your persona"}
                </Typography>
                {several ? (
                    <>
                        <CCInputLabel>Account</CCInputLabel>
                        <CCSelect
                            size={"small"}
                            fullWidth={true}
                            value={accountName}
                            onChange={(e: any) => {
                                setAccountName(e.target.value);
                                setVisibility(defaultVisibility(accounts, e.target.value));
                            }}>
                            {names.map(name => (
                                <MenuItem value={name} key={`sso-account-${name}`}>{name}</MenuItem>
                            ))}
                        </CCSelect>
                    </>
                ) : null}
                <PersonaSelect
                    positions={account?.["Positions"] ?? []}
                    positionAncestors={account?.["PositionAncestors"] ?? []}
                    userGroups={account?.["UserGroups"] ?? []}
                    workGroups={account?.["WorkGroups"] ?? []}
                    valuePrefix={`${account?.["AccountUSID"]}:`}
                    value={visibility}
                    onChange={setVisibility}/>
                <Button
                    className={css_buttons.blueStandardButton}
                    style={{width: "100%", marginTop: "20px", marginBottom: "5px"}}
                    disabled={submitting || !visibility}
                    onClick={() => onChoose(visibility)}
                >
                    Continue
                </Button>
                <div style={{height: "10px"}}/>
            </DialogContent>
        </Dialog>
    );
}
